import { getCurrencyFromRegion } from "@Components/LeadFormModal/utils";
import { MONTHS as months } from "constants/index";
import {
  FILTERS,
  ROOM_TYPE_COMBINED,
  RoomTypeCombined,
  STAY_DURATION_COMMON,
  STAY_DURATION_US,
} from "../components/FilterSection/data/filtersList";
import { getPricingCurrency } from "../components/SearchListItem/helper";
import { FilterState } from "./formatFilterState";
import { readStoredSelection } from "../components/FilterSection/components/Filters/Locality/localitySelection";
import { formatMoveInMonth } from "@Pages/PropertyPage/data/helper";
import currencyData from "constants/currencyConstants";
import { formatAmenityName } from "../components/FilterSection/components/Filters/AmenitiesFilter/utils/formatAmenityName";
import config from "@Config/index";
import { renderChinaNamedMonth } from "@Utils/cnDisplayDate";

export type FilterNameType = (typeof FILTERS)[number]["name"] | "Filters";

export type TranslateFn = (key: string) => string;

export const translateFilterOptionLabel = (label: string, t?: TranslateFn): string =>
  t ? t(label) : label;

export const getBudgetLabel = (
  filterState: FilterState,
  region: any,
  currencyExchangeRates?: any,
  /** Top of the price histogram, so an open-ended budget reads "£40 - £660+" not "£40+". */
  priceCeiling?: number,
) => {
  const regionCurrency = getPricingCurrency(getCurrencyFromRegion(region?.combined_name));
  const currencyObject = currencyData?.find((currObj) => currObj?.symbol === regionCurrency);

  const exchangeRate = currencyExchangeRates?.rates?.find((item: any) => {
    const target = item?.target_currency?.toLowerCase();
    return target === currencyObject?.standard_name?.toLowerCase();
  });
  const exchangeCurrencyObject = currencyData?.find(
    (currObj) => currObj?.standard_name === currencyExchangeRates?.baseCurrency,
  );

  const currency = currencyExchangeRates?.baseCurrency
    ? exchangeCurrencyObject?.symbol
    : regionCurrency;

  const [min, max] = Array.isArray(filterState.budget)
    ? filterState.budget.map(String)
    : String(filterState.budget)?.split(",");
  const formatAmount = (amount: string) =>
    exchangeRate?.rate ? Math.ceil(Number(amount) / exchangeRate.rate) : amount;

  if (!min || !max) return "Incorrect Value";
  // The panel cannot select past the histogram's top — anything above it is the catch-all, closed
  // at the region's dearest price. Name the top the user actually saw: "£140 - £660+".
  // `Number(min) < priceCeiling` guards the case where another filter has narrowed the histogram
  // below the applied budget: with a budget of 340-3658 and a ceiling of 125 this printed
  // "£340 - £125+", a range whose start is past its end. There the real numbers are the honest
  // label, and the "+" belongs only to a range the ceiling actually bounds.
  if (
    Number.isFinite(priceCeiling) &&
    Number(max) >= (priceCeiling as number) &&
    Number(min) < (priceCeiling as number)
  ) {
    return `${currency}${formatAmount(min)} - ${currency}${formatAmount(String(priceCeiling))}+`;
  }
  return `${currency}${formatAmount(min)} - ${currency}${formatAmount(max)}`;
};

export const getMoveInMonthLabel = (filterState: FilterState) => {
  const formattedMoveInMonth = formatMoveInMonth(filterState.moveInMonth);
  const value = `01-${String(formattedMoveInMonth)}`;
  const date = new Date(value);
  const month = months[date.getMonth()] || null;
  const year = date.getFullYear() || null;

  const label = month && year ? `${month}, ${year}` : `Incorrect Value`;
  if (config.IS_CHINA_APP && filterState.moveInMonth) {
    return renderChinaNamedMonth(filterState.moveInMonth, label) || label;
  }
  return label;
};

export const getStayDurationLabel = (filterState: FilterState, t?: TranslateFn) => {
  if (filterState.minLeaseDuration === "all_selected") {
    return "";
  }

  const stayDurationTypes = [...STAY_DURATION_COMMON, ...STAY_DURATION_US];
  const activeLeaseDuration = stayDurationTypes.find((duration) =>
    duration.value.includes(String(filterState.minLeaseDuration)),
  );

  return translateFilterOptionLabel(activeLeaseDuration?.name || "Incorrect Value", t);
};

export const getRoomTypeLabel = (filterState: FilterState, t?: TranslateFn) => {
  const roomTypesList = ROOM_TYPE_COMBINED.reduce(
    (acc: RoomTypeCombined["list"], curr) => [...acc, ...curr.list],
    [],
  );

  const roomTypeListFiltered = roomTypesList.filter((item) =>
    filterState.roomType?.includes(item.value),
  );

  const selectedRoomTypes = roomTypeListFiltered.map((item) =>
    translateFilterOptionLabel(item.name, t),
  );
  const label =
    selectedRoomTypes.length > 1
      ? `${selectedRoomTypes[0]} +${selectedRoomTypes.length - 1}`
      : selectedRoomTypes[0];

  return label;
};

export const getUniversityLabel = (
  filterState: FilterState,
  universityList?: Array<{ label: string; value: string }>,
) => {
  const canonicalName = filterState?.university as string;
  if (!canonicalName) return "";

  // If we have the fetched list (with translated labels), use it directly
  if (universityList && universityList.length > 0) {
    const match = universityList.find((u) => u.value === canonicalName);
    if (match) return match.label;
  }

  // Fallback: reconstruct from canonical slug
  const canonicalNameWithoutNum = canonicalName?.slice(0, canonicalName.lastIndexOf("-"));
  const university = canonicalNameWithoutNum
    ?.replace(/-/g, " ")
    ?.replace(/(^\w{1})|(\s{1}\w{1})/g, (match) => match?.toUpperCase()); // Capitalize first character after space
  return university || "";
};

export const getLocalityLabel = (
  filterState: FilterState,
  localityList?: Array<{ label: string; value: string }>,
) => {
  const canonicalName = filterState?.locality as string;
  if (!canonicalName) return "";

  // When multi-select is active, the selection lives in sessionStorage (see
  // Locality.tsx). The badge shows "First +N" like Amenities. If no stored
  // selection exists, fall through to the single-select label.
  const stored = readStoredSelection();
  if (stored.length > 0) {
    return stored.length > 1 ? `${stored[0].label} +${stored.length - 1}` : stored[0].label;
  }

  // If we have the fetched list (with translated labels), use it directly
  if (localityList && localityList.length > 0) {
    const match = localityList.find((l) => l.value === canonicalName);
    if (match) return match.label;
  }

  const canonicalNameWithoutNum = canonicalName?.slice(0, canonicalName.lastIndexOf("-"));

  const locality = canonicalNameWithoutNum
    ?.replace(/-/g, " ")
    ?.replace(/(^\w{1})|(\s{1}\w{1})/g, (match) => match?.toUpperCase()); // Capitalize first character after space

  return locality || "";
};

export const getHousingTypeLabel = (filterState: FilterState, t?: TranslateFn) => {
  const housingType = filterState?.housingType as string;

  const labels: Record<string, string> = {
    all: "All Accommodations",
    university_provider: "University Housing",
    private: "Private Housing",
  };

  return translateFilterOptionLabel(labels[housingType] || labels.all, t);
};

export const getAmenitiesLabel = (filterState: FilterState, t?: TranslateFn) => {
  const amenities = filterState?.amenities as string[];
  if (!amenities || amenities.length === 0) return "";

  const firstAmenity = translateFilterOptionLabel(formatAmenityName(amenities[0]), t);
  return amenities.length > 1 ? `${firstAmenity} +${amenities.length - 1}` : firstAmenity;
};

export const getBillsIncludedLabel = (filterState: FilterState, t?: TranslateFn) => {
  const billsIncluded = filterState?.billsIncluded as string[];
  if (!billsIncluded || billsIncluded.length === 0) return "";

  const firstName = translateFilterOptionLabel(formatAmenityName(billsIncluded[0]), t);
  return billsIncluded.length > 1 ? `${firstName} +${billsIncluded.length - 1}` : firstName;
};

const getFilterLabel = (
  filterName: FilterNameType,
  filterState: FilterState,
  region?: any,
  currencyExchangeRates?: any,
  t?: TranslateFn,
  priceCeiling?: number,
  universityList?: Array<{ label: string; value: string }>,
  localityList?: Array<{ label: string; value: string }>,
) => {
  switch (filterName) {
    case "Budget":
      return getBudgetLabel(filterState, region, currencyExchangeRates, priceCeiling);

    case "Move in Month":
      return getMoveInMonthLabel(filterState);

    case "Stay Duration":
      return getStayDurationLabel(filterState, t);

    case "Room Type":
      return getRoomTypeLabel(filterState, t);

    case "University":
      return getUniversityLabel(filterState, universityList);

    case "Locality":
      return getLocalityLabel(filterState, localityList);

    case "Housing Type":
      return getHousingTypeLabel(filterState, t);

    case "Amenities":
      return getAmenitiesLabel(filterState, t);

    case "Bills Included":
      return getBillsIncludedLabel(filterState, t);

    case "Housing Fest":
      return "";

    default:
      return "";
  }
};

export default getFilterLabel;
